import { Cancel, CheckCircle, CloseOutlined } from '@mui/icons-material';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Fragment, useState } from 'react';

import { trpc } from '../../trpc';
import { TopicVACard } from './TopicVACard';

const getSapIds = (input: string): number[] => {
  const values = input.split(',').map((value) => value.trim());

  const newIds: number[] = [];
  for (const value of values) {
    const num = Number(value);
    if (Number.isInteger(num) && num > 1_000) {
      newIds.push(num);
    }
  }

  return newIds;
};

const allowedChars = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', ',', ' '];

export const VATopicPage = () => {
  const [input, setInput] = useState('');
  const [sapids, setSapids] = useState<number[]>([]);
  const topicVeranstaltungQuery = trpc.veranstaltung.getTopicVeranstaltung.useQuery(
    { sapids },
    { enabled: sapids.length > 0 },
  );
  const vas = topicVeranstaltungQuery.data ?? [];
  const retriggerMutation = trpc.veranstaltung.retriggerVeranstaltung.useMutation();

  const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    let cleanedValue = '';
    for (const char of value) {
      if (allowedChars.includes(char)) {
        cleanedValue += char;
      }
    }

    if (sapids.length > 0) {
      setSapids([]);
    }

    setInput(cleanedValue);
  };

  const retriggerVeranstaltungen = async () => {
    retriggerMutation.reset();
    await retriggerMutation.mutateAsync({ sapids: getSapIds(input) });
  };

  const loadVeranstaltungen = () => {
    retriggerMutation.reset();
    setSapids(getSapIds(input));
  };

  return (
    <Stack direction="column" spacing={2}>
      <Box padding={2} display="flex" gap={2} justifyContent="center" alignItems="center">
        <TextField
          sx={{ width: '50vw' }}
          label="SAP-ID"
          helperText="Kommagetrennt 61143060,61143061,..."
          variant="standard"
          value={input}
          onChange={onInputChange}
        />
        <Button size="small" variant="contained" onClick={() => loadVeranstaltungen()} disabled={input.length < 5}>
          {topicVeranstaltungQuery.isLoading ? <CircularProgress size="1.5rem" color="secondary" /> : 'Anzeigen'}
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={async () => await retriggerVeranstaltungen()}
          disabled={input.length < 5}
        >
          {retriggerMutation.isPending ? <CircularProgress size="1.5rem" color="secondary" /> : 'Veröffentlichen'}
        </Button>
      </Box>

      {topicVeranstaltungQuery.isError && (
        <Box sx={{ marginBottom: 2 }}>
          <Alert severity="error">
            <AlertTitle>Fehler beim Laden der Veranstaltungen</AlertTitle>
            {topicVeranstaltungQuery.error.message}
          </Alert>
        </Box>
      )}

      {topicVeranstaltungQuery.data && sapids.length !== topicVeranstaltungQuery.data.length && (
        <Box sx={{ marginBottom: 2 }}>
          <Alert severity="warning">
            <AlertTitle>Veranstaltungen nicht gefunden</AlertTitle>
            {sapids.filter((id) => !vas.some(({ sapObjectId }) => sapObjectId === id)).join(',')}
          </Alert>
        </Box>
      )}

      {retriggerMutation.isSuccess && (
        <Paper sx={{ padding: 2 }}>
          <Box display="flex" flexDirection="row" alignItems="center" padding={1}>
            <Typography variant="headingXxs" width="100%">
              Topic-Veröffentlichungs-Ergebnisse
            </Typography>
            <IconButton size="small" onClick={() => retriggerMutation.reset()}>
              <CloseOutlined />
            </IconButton>
          </Box>
          <Divider sx={{ marginBottom: 2 }} />
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(1, 1.5rem 6rem 1fr)',
              width: '100%',
              alignItems: 'center',
            }}
          >
            {retriggerMutation.data.map((result) => (
              <Fragment key={result.sapid}>
                {result.success ? (
                  <CheckCircle fontSize="small" color="success" />
                ) : (
                  <Cancel fontSize="small" color="error" />
                )}
                <Typography variant="paragraph" fontWeight="bold">
                  {result.sapid}
                </Typography>
                <Typography variant="paragraph">{result.msg}</Typography>
              </Fragment>
            ))}
          </Box>
        </Paper>
      )}

      {vas.length > 0 ? <TopicVACard topicVeranstaltungen={vas} /> : null}
    </Stack>
  );
};
